import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  normalizeReceivingTime,
  normalizeCases,
  fmtDay,
  fmtTime,
  fmtDayTime,
  fmtInstant,
  fmtLongDate,
  trendHeights,
  visibleTrend,
  weekTick,
  isoDay,
  EXPORT_COLUMNS,
  toExportRows,
} from "./receivingTimeHelpers";
import { exportRowsToExcel } from "./exportToExcel";
import "./ReceivingTime.css";

// "Receiving Time" widget — how many trucks the Denver warehouse received this
// week and how long, on average, it took to get each delivery's material into
// bins. Source: the Spot AI receiving cases the camera watcher logs, which the API
// keeps a copy of (SpotAiReceivingSyncService) and summarises (ReceivingTimeModule).
//
// Firm-wide metric, so dataScope is ignored. The week is the dashboard's
// Thursday-to-Wednesday reporting week; the bars show the weeks before it.
//
// Clicking the numbers or a bar opens every case for that week, with all of its
// Spot AI details (fields, clips, status changes, comments), and an Excel export.
// A truck that hasn't been put away is counted up to now, so its time keeps
// growing until the put-away time is entered in Spot AI.

const ReceivingTime = ({ auth: { auth } }) => {
  const token = auth && auth[0] ? auth[0].token : null;

  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("loading");

  const [weekIndex, setWeekIndex] = useState(null); // index into summary.trend
  const [cases, setCases] = useState([]);
  const [casesStatus, setCasesStatus] = useState("idle");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setStatus("loading");
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/ReceivingTime`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        setSummary(normalizeReceivingTime(res.data));
        setStatus("ok");
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Receiving Time:", err.message);
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const trend = summary ? visibleTrend(summary.trend) : [];
  const week = summary ? summary.currentWeek : null;
  const selected = weekIndex != null ? trend[weekIndex] : null;

  // Load the selected week's cases whenever the modal moves to a different week.
  useEffect(() => {
    if (weekIndex == null || !token || !trend[weekIndex]) return undefined;
    let active = true;
    setCasesStatus("loading");
    setExpanded({});
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/ReceivingTimeDetail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { weekStart: isoDay(trend[weekIndex].weekStart) },
      })
      .then((res) => {
        if (!active) return;
        setCases(normalizeCases(res.data));
        setCasesStatus("ok");
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Receiving Time detail:", err.message);
        setCasesStatus("error");
      });
    return () => {
      active = false;
    };
    // trend is stable for the life of the modal; weekIndex drives the reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekIndex, token]);

  const openWeek = (index) => {
    if (index == null || index < 0 || index >= trend.length) return;
    setWeekIndex(index);
  };
  const openCurrent = () => openWeek(trend.length - 1);
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const exportCases = () =>
    exportRowsToExcel({
      columns: EXPORT_COLUMNS,
      rows: toExportRows(cases),
      fileName: `Receiving Time ${selected ? selected.label : ""}`,
      sheetName: "Receiving Time",
    });

  // ------------------------------------------------------------- tile states
  let body;
  if (status === "loading") {
    body = <div className="rt-state">Loading…</div>;
  } else if (status === "error") {
    body = <div className="rt-state rt-error">Unable to load data.</div>;
  } else if (!summary.connected && !summary.hasData) {
    body = (
      <div className="rt-state">
        {"Spot AI receiving data isn't connected yet. This fills in once Nancy can read the receiving cases."}
      </div>
    );
  } else {
    const heights = trendHeights(trend);
    body = (
      <>
        {summary.syncProblem && <div className="rt-warning">{summary.syncProblem}</div>}
        <div className="rt-week">This week · {week.label}</div>
        <div className="rt-cards" onClick={openCurrent} role="button" title="View this week's trucks">
          <div className="rt-card">
            <div className="rt-card-value">{week.trucksReceived}</div>
            <div className="rt-card-label">Trucks Received</div>
          </div>
          <div className="rt-card">
            <div className="rt-card-value">{week.averageDisplay}</div>
            <div className="rt-card-label">Avg Time to Put Away</div>
          </div>
        </div>
        {week.notPutAway > 0 && (
          <div className="rt-note">
            {week.notPutAway} {week.notPutAway === 1 ? "truck isn't" : "trucks aren't"} put away yet and
            {week.notPutAway === 1 ? " is" : " are"} counted up to now.
          </div>
        )}
        <div className="rt-trend" aria-label="Average time to put away, by week">
          {trend.map((w, i) => (
            <button
              type="button"
              key={w.weekStart}
              className={`rt-bar-wrap${i === trend.length - 1 ? " rt-current" : ""}`}
              onClick={() => openWeek(i)}
              title={`${w.label}: ${w.trucksReceived} truck${w.trucksReceived === 1 ? "" : "s"}, average ${w.averageDisplay}`}
            >
              <span className="rt-bar-value">{w.trucksReceived > 0 ? w.averageDisplay : ""}</span>
              <span className="rt-bar" style={{ height: `${heights[i]}%` }} />
              <span className="rt-bar-tick">{weekTick(w)}</span>
              <span className="rt-bar-count">{w.trucksReceived}</span>
            </button>
          ))}
        </div>
        <div className="rt-legend">Bars: average time to put away · number below: trucks · click any week for details</div>
      </>
    );
  }

  return (
    <div className="rt-widget">
      <h4 className="rt-title">Receiving Time</h4>
      {body}

      <Modal
        show={weekIndex != null}
        onHide={() => setWeekIndex(null)}
        size="xl"
        centered
        scrollable
        dialogClassName="ft-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Receiving Time · {selected ? selected.label : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="rt-modal-bar">
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={weekIndex == null || weekIndex <= 0} onClick={() => openWeek(weekIndex - 1)}>
              ‹ Previous week
            </button>
            {selected && (
              <span className="rt-modal-summary">
                {selected.trucksReceived} truck{selected.trucksReceived === 1 ? "" : "s"} · average{" "}
                {selected.averageDisplay}
                {selected.notPutAway > 0 ? ` · ${selected.notPutAway} not put away yet` : ""}
                {selected.flagged > 0 ? ` · ${selected.flagged} need a look` : ""}
              </span>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={weekIndex == null || weekIndex >= trend.length - 1} onClick={() => openWeek(weekIndex + 1)}>
              Next week ›
            </button>
            <button type="button" className="btn btn-sm btn-success rt-export"
              disabled={casesStatus !== "ok" || cases.length === 0} onClick={exportCases}>
              Export to Excel
            </button>
          </div>

          {casesStatus === "loading" ? (
            <div className="ft-status">Loading…</div>
          ) : casesStatus === "error" ? (
            <div className="ft-status">{"Could not load this week's trucks."}</div>
          ) : cases.length === 0 ? (
            <div className="ft-status">No trucks were logged in Spot AI for this week.</div>
          ) : (
            <table className="table table-sm rt-table">
              <thead>
                <tr>
                  <th />
                  <th>Truck</th>
                  <th>Received</th>
                  <th>Supplier</th>
                  <th>Material</th>
                  <th className="rt-num">Pallets</th>
                  <th className="rt-num">Rolls</th>
                  <th>Clock Started</th>
                  <th>Put Away</th>
                  <th className="rt-num">Time to Put Away</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <Fragment key={c.caseId}>
                    <tr
                      className={`rt-row${!c.isPutAway ? " rt-open" : ""}${c.flags.length ? " rt-flagged" : ""}`}
                      onClick={() => toggle(c.caseId)}
                    >
                      <td className="rt-caret">{expanded[c.caseId] ? "▾" : "▸"}</td>
                      <td>{c.title}</td>
                      <td>{fmtDay(c.incidentDate)}</td>
                      <td>{c.supplier || "—"}</td>
                      <td>{c.materialType || "—"}</td>
                      <td className="rt-num">{c.pallets || "—"}</td>
                      <td className="rt-num">{c.rolls || "—"}</td>
                      <td>
                        {fmtTime(c.startLocal)}
                        {c.startFromCaseCreated && <span className="rt-star" title="No unload time entered; clock started when the case was opened">*</span>}
                      </td>
                      <td>{c.isPutAway ? fmtDayTime(c.putAwayLocal) : <span className="rt-badge rt-badge-open">Not yet</span>}</td>
                      <td className="rt-num">
                        {c.countsInAverage ? (
                          <>
                            {c.durationDisplay}
                            {!c.isPutAway && <span className="rt-sofar"> so far</span>}
                          </>
                        ) : (
                          <span className="rt-badge rt-badge-warn">Check times</span>
                        )}
                      </td>
                      <td>
                        {c.status === "closed" ? "Closed" : "Open"}
                        {c.flags.length > 0 && (
                          <span className="rt-badge rt-badge-warn" title={c.flags.join(" ")}>⚠ {c.flags.length}</span>
                        )}
                      </td>
                    </tr>
                    {expanded[c.caseId] && (
                      <tr className="rt-detail-row">
                        <td />
                        <td colSpan={10}>
                          <CaseDetail c={c} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          <div className="rt-footnote">
            Denver time. The clock runs from when the last item came off the truck to when the last item went into a
            bin. * no unload time was entered, so the clock starts when the case was opened in Spot AI. Trucks not put
            away yet are counted up to now. Click a truck for everything on its Spot AI case.
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Everything on the Spot AI case, as the watcher entered it.
const CaseDetail = ({ c }) => {
  const d = c.detail;
  return (
    <div className="rt-detail">
      {c.flags.length > 0 && (
        <ul className="rt-flags">
          {c.flags.map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      )}

      <div className="rt-detail-grid">
        <div>
          <div className="rt-detail-head">Case</div>
          <dl className="rt-dl">
            <dt>Title</dt><dd>{c.title}</dd>
            <dt>Incident date</dt><dd>{fmtLongDate(c.incidentDate)}</dd>
            <dt>Status</dt><dd>{c.status === "closed" ? "Closed" : "Open"}</dd>
            <dt>Location</dt><dd>{c.location || "—"}</dd>
            <dt>Logged by</dt><dd>{c.createdBy || "—"}, {fmtDayTime(c.createdAtLocal)}</dd>
            <dt>Description</dt><dd>{c.description || "—"}</dd>
            {d.tags.length > 0 && (<><dt>Tags</dt><dd>{d.tags.join(", ")}</dd></>)}
          </dl>
          {c.spotAiUrl && (
            <a href={c.spotAiUrl} target="_blank" rel="noopener noreferrer">Open this case in Spot AI ↗</a>
          )}
        </div>

        <div>
          <div className="rt-detail-head">Case details</div>
          <dl className="rt-dl">
            {d.fields.map((f) => (
              <Fragment key={f.label}>
                <dt>{f.label}</dt>
                <dd className="rt-pre">{f.value || "—"}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      </div>

      <div className="rt-detail-grid">
        <div>
          <div className="rt-detail-head">Clips ({d.clips.length})</div>
          {d.clips.length === 0 ? (
            <div className="rt-muted">No clips.</div>
          ) : (
            <ul className="rt-list">
              {d.clips.map((k, i) => (
                <li key={`${k.camera}-${k.startUtc}-${i}`}>
                  {fmtInstant(k.startUtc)} · {k.camera}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="rt-detail-head">Activity</div>
          {d.events.length === 0 && d.comments.length === 0 && d.annotations.length === 0 ? (
            <div className="rt-muted">No comments or status changes.</div>
          ) : (
            <ul className="rt-list">
              {d.events.map((e, i) => (
                <li key={`e${i}`}>{fmtInstant(e.atUtc)} · {e.by}: {e.description}</li>
              ))}
              {d.comments.map((n, i) => (
                <li key={`c${i}`}>{fmtInstant(n.atUtc)} · {n.by} commented: {n.text}</li>
              ))}
              {d.annotations.map((n, i) => (
                <li key={`a${i}`}>{fmtInstant(n.atUtc)} · {n.by} annotated {n.label ? `"${n.label}"` : ""}: {n.text}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export { ReceivingTime };

export default connect(mapStateToProps)(ReceivingTime);
